import type { FavouriteProduct } from '@/scopes/favourites/domain/favouriteTypes'
import { Rating } from '@sunwebgroup/uds-ui/rating'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from 'react-intl'
import { MediaServerLazyImage } from '@/scopes/common/components/MediaServerLazyImage'
import { FavoriteToggleButton } from '@/scopes/favourites/components/FavoriteToggleButton/FavoriteToggleButton'
import { UserRating } from '@/scopes/search/search/pages/SearchPage/components/Results/ProductCard/ScoresRatings'
import styles from './FavoriteCard.module.css'

interface FavoriteCardProps {
  product: FavouriteProduct
  isRemoving: boolean
  onRemove: (productId: number) => void
}

export function FavoriteCard({
  product,
  isRemoving,
  onRemove,
}: FavoriteCardProps) {
  const intl = useIntl()
  const href = product.urlWithFilters || product.url
  const locations = product.locations.map(location => location.name).join(' > ')

  return (
    <article aria-busy={isRemoving} className={styles.card}>
      <a className={styles.cardLink} href={href} aria-label={product.name} />
      <div className={styles.imageWrapper}>
        {product.images[0] && (
          <MediaServerLazyImage
            alt={product.name}
            aspectRatio="4:3"
            className={styles.image}
            src={product.images[0]}
            widths={[480, 640]}
          />
        )}
        <FavoriteToggleButton
          aria-label={intl.formatMessage({ id: 'favorite_page_remove_action' })}
          className={styles.removeButton}
          disabled={isRemoving}
          isFavourite
          onClick={() => onRemove(product.id)}
        />
      </div>
      <div className={styles.content}>
        <div className={styles.ratings}>
          <Rating rating={product.rating} />
          {product.userRating != null && product.userRating > 0 && (
            <UserRating userRating={product.userRating} />
          )}
        </div>
        <Typography as="h2" variant="display-g-regular">{product.name}</Typography>
        <Typography as="p" variant="body-s-regular-snug">{locations}</Typography>
        <ul className={styles.usps}>
          {product.usps.map(usp => (
            <li key={usp}>
              <Typography as="span" variant="body-s-regular-snug">{usp}</Typography>
            </li>
          ))}
        </ul>
      </div>
    </article>
  )
}
